import React, { useState, useRef, useEffect, useCallback } from "react";
import * as api from "../../api";
import { ReactComponent as CloseIcon } from "../../assets/images/cross.svg";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const OtpVerification = ({
  email = "",
  onVerified = () => {},
  close = () => {},
}) => {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...digits];
    updated[index] = value;
    setDigits(updated);
    setFormError("");
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => {
      updated[i] = char;
    });
    setDigits(updated);
    setFormError("");
    const nextEmpty = pasted.length < OTP_LENGTH ? pasted.length : OTP_LENGTH - 1;
    inputRefs.current[nextEmpty]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length < OTP_LENGTH) {
      setFormError("Please enter the full 6-digit code.");
      return;
    }
    setFormError("");
    setIsSubmitting(true);
    try {
      const response = await api.auth.verifyOtp(email, otp);
      if (response.status >= 200 && response.status < 300) {
        onVerified(response.data);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        const data = error.response.data;
        const msg =
          data?.nonFieldErrors?.[0] ||
          data?.otp?.[0] ||
          data?.detail ||
          "Invalid OTP. Please try again.";
        setFormError(msg);
      } else {
        setFormError("Something went wrong. Please try again.");
      }
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setFormError("");
    try {
      await api.auth.resendOtp(email);
      startCooldown();
    } catch {
      setFormError("Failed to resend OTP. Please try again.");
    }
  };

  return (
    <React.Fragment>
      <div className="popup">
        <CloseIcon className="popup__close" onClick={() => close()} />
        <div className="popup__body popup__body--registration">
          <form className="registration-form registration-form--otp" onSubmit={handleSubmit}>
            <h3 className="registration-form__title">Verify your email</h3>
            <p className="registration-form__otp-subtitle">
              We sent a 6-digit code to{" "}
              <span className="registration-form__new-user-email">{email}</span>
            </p>
            {formError && (
              <p className="error-message registration-form__form-error">
                {formError}
              </p>
            )}
            <div className="registration-form__otp-inputs">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="registration-form__otp-box"
                  autoComplete="off"
                />
              ))}
            </div>
            <button
              type="submit"
              className="btn btn--primary registration-form__btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Verifying..." : "Verify"}
            </button>
            <p className="registration-form__redirect registration-form__redirect--paragraph">
              Didn't receive the code?{" "}
              <span
                className={
                  "registration-form__redirect--btn" +
                  (cooldown > 0 ? " registration-form__redirect--btn-disabled" : "")
                }
                onClick={handleResend}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
              </span>
            </p>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default OtpVerification;
